import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { Row, Button } from 'antd'
import { CaretLeftOutlined, CaretRightOutlined } from '@ant-design/icons'
import * as d3 from 'd3'
import moment from 'moment'
import '../../../css/patient.scss'
import { getTracpatchExerciseResults } from '../../../store/action/patient.action'
import { styleAxisTicks, AXIS_TITLE_FONT_SIZE, AXIS_TITLE_FONT_WEIGHT } from './tracpatch.chart.style'
import { earliestDay, isTodayOrLater, compactHourLabel } from './tracpatch.hourly'
import { effectiveWindow, staleRangeLabel } from './tracpatch.window'
import { DataViewToggle, ChartDataGrid } from '../../graph/chartDataView'

// Total TracPatch exercise reps, stacked by exercise.
//
// One bar per day (30 / 14 / 7 Days) or per hour (1 Day). Each segment is the
// sum of repsCompleted for one exercise in that bucket, across every set —
// overflow sets past the prescription included, same as the per-exercise reps
// chart (tracpatch.exercise.reps.chart.js). This chart answers "how much did
// the patient do, and of what"; the per-exercise charts below it answer "did
// they meet the prescription".
//
// Colours follow the EXERCISE, not its rank in the window: exercises are
// ordered by name across all of the patient's records and take palette slots
// in that order, so switching 30 → 7 Days never repaints a survivor. Palette
// is the validated 8-slot categorical set (adjacent-pair CVD ΔE ≥ 9.1 on
// white). A ninth exercise folds into "Other" rather than inventing a hue.
//
// Three slots sit below 3:1 contrast on white, which is why the data-table
// view is not optional here: it is the colour-free way to read the chart.

const SERIES_COLORS = [
  '#2a78d6', // blue
  '#eb6834', // orange
  '#1baf7a', // aqua
  '#eda100', // yellow
  '#e87ba4', // magenta
  '#008300', // green
  '#6250d6', // violet
  '#e34948', // red
]
const OTHER = { key: '__other', label: 'Other', color: '#8c8c8c' }

const MODES = {
  D1:  { id: 'D1',  bars: 24, intervalMs: 3600 * 1000,      daily: false },
  D7:  { id: 'D7',  bars: 7,  intervalMs: 24 * 3600 * 1000, daily: true },
  D14: { id: 'D14', bars: 14, intervalMs: 24 * 3600 * 1000, daily: true },
  D30: { id: 'D30', bars: 30, intervalMs: 24 * 3600 * 1000, daily: true },
}

// "28/30 reps" when the prescription is known, "28 reps" when it is not (sets
// saved without requiredReps / requiredSets).
const repsText = (done, target) => (target > 0 ? `${done}/${target} reps` : `${done} reps`)

const exerciseKey = (r) =>
  r.exerciseId != null ? String(r.exerciseId) : (r.exerciseName || 'unknown')

// Stable series list for the patient's whole history (see header).
const buildSeries = (records) => {
  const names = {}
  records.forEach((r) => {
    const k = exerciseKey(r)
    if (!names[k]) names[k] = r.exerciseName || 'TracPatch Exercise'
  })
  const keys = Object.keys(names).sort((a, b) => names[a].localeCompare(names[b]))
  const series = keys.slice(0, SERIES_COLORS.length).map((k, i) => ({
    key: k,
    label: names[k],
    color: SERIES_COLORS[i],
  }))
  if (keys.length > SERIES_COLORS.length) series.push(OTHER)
  const seriesFor = {}
  keys.forEach((k, i) => {
    seriesFor[k] = i < SERIES_COLORS.length ? k : OTHER.key
  })
  return { series, seriesFor }
}

const TracPatchExerciseStackedChart = (props) => {
  const svgRef = useRef(null)
  const containerRef = useRef(null)
  const tooltipRef = useRef(null)
  const [records, setRecords] = useState([])
  const [modeId, setModeId] = useState('D30')
  const [view, setView] = useState('chart')
  // The day the 1 Day view renders. Today until a daily bar is clicked.
  const [day, setDay] = useState(() => moment().startOf('day'))

  useEffect(() => {
    let mounted = true
    const load = async () => {
      if (!props.patientId) return
      const res = await props.getTracpatchExerciseResults(props.patientId)
      if (mounted) setRecords((res && res.data) || [])
    }
    load()
    return () => {
      mounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.patientId, props.refreshKey])

  useEffect(() => {
    if (view === 'chart') draw()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records, modeId, view, day.valueOf()])

  const { series, seriesFor } = buildSeries(records)

  // Only sets that carry reps ever land in a bucket (see the fill loop), so the
  // empty-window fallback measures exactly those — otherwise it could fall back
  // to a day whose only records are zero-rep sets and still draw nothing.
  const repDate = (r) => (r && r.completedAt && r.repsCompleted > 0 ? r.completedAt : null)

  const buildBuckets = () => {
    const mode = MODES[modeId]
    // Daily views fall back to the patient's latest session when the requested
    // range holds none — see tracpatch.window.js. The 1 Day view is driven by
    // the day picker, so it keeps its own start.
    let windowStart = mode.daily
      ? effectiveWindow(records, mode.bars, repDate).windowStart.clone().startOf('day')
      : moment(day).startOf('day')

    const buckets = []
    let cursor = windowStart.clone()
    for (let i = 0; i < mode.bars; i++) {
      const reps = {}
      const target = {}
      series.forEach((s) => { reps[s.key] = 0; target[s.key] = 0 })
      buckets.push({ index: i, start: cursor.toDate(), reps, total: 0, target, targetTotal: 0, targeted: {} })
      cursor = cursor.clone().add(mode.intervalMs, 'ms')
    }

    const startMs = windowStart.valueOf()
    records.forEach((r) => {
      if (!r.completedAt || !(r.repsCompleted > 0)) return
      const ms = new Date(r.completedAt).getTime() - startMs
      if (ms < 0 || ms >= mode.bars * mode.intervalMs) return
      const b = buckets[Math.floor(ms / mode.intervalMs)]
      const key = seriesFor[exerciseKey(r)]
      b.reps[key] += r.repsCompleted
      b.total += r.repsCompleted
      // The day's prescription, requiredReps × requiredSets, taken once per
      // exercise from its first set of the day — the same rule as the
      // per-exercise reps chart. Hourly buckets are filled after this loop.
      const exKey = exerciseKey(r)
      if (mode.daily && !b.targeted[exKey]) {
        b.targeted[exKey] = true
        const t = (r.requiredReps || 0) * (r.requiredSets || 0)
        b.target[key] += t
        b.targetTotal += t
      }
    })

    // 1 Day: nothing is prescribed per hour, so each hour is measured against
    // the DAY's prescription — "15/30" at 9 AM means 15 of the day's 30 reps
    // were done in that hour. The day's own totals ride on every bucket for
    // the tooltip header.
    let dayTotals = null
    if (!mode.daily) {
      dayTotals = { total: 0, target: 0 }
      const dayTarget = {}
      records.forEach((r) => {
        if (!r.completedAt || !(r.repsCompleted > 0)) return
        const ms = new Date(r.completedAt).getTime() - startMs
        if (ms < 0 || ms >= mode.bars * mode.intervalMs) return
        dayTotals.total += r.repsCompleted
        const exKey = exerciseKey(r)
        if (dayTarget[exKey] == null) {
          dayTarget[exKey] = (r.requiredReps || 0) * (r.requiredSets || 0)
          dayTotals.target += dayTarget[exKey]
        }
      })
      buckets.forEach((b) => {
        Object.keys(dayTarget).forEach((exKey) => {
          const key = seriesFor[exKey]
          if (b.reps[key] > 0 && !b.targeted[exKey]) {
            b.targeted[exKey] = true
            b.target[key] += dayTarget[exKey]
          }
        })
        b.day = dayTotals
      })
    }

    // Daily views start at the first day with reps, as the other TracPatch
    // charts do, instead of a month of blank chart before a new patient's data.
    if (mode.daily) {
      let firstIdx = 0
      while (firstIdx < buckets.length - 1 && buckets[firstIdx].total === 0) firstIdx++
      if (firstIdx > 0) {
        buckets.splice(0, firstIdx)
        buckets.forEach((b, i) => { b.index = i })
      }
    }
    return { buckets, mode }
  }

  const draw = () => {
    const { buckets, mode } = buildBuckets()
    if (!svgRef.current || !buckets.some((b) => b.total > 0)) return

    const margin = { top: 16, right: 16, bottom: 64, left: 52 }
    const screenWidth = containerRef.current ? containerRef.current.offsetWidth : 400
    const screenHeight = 320
    const width = screenWidth - margin.left - margin.right
    const height = screenHeight - margin.top - margin.bottom

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()
    const tooltip = d3.select(tooltipRef.current)
    tooltip.style('visibility', 'hidden')

    const g = svg
      .attr('width', screenWidth)
      .attr('height', screenHeight)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    const x = d3.scaleBand()
      .domain(buckets.map((b) => b.index))
      .range([0, width])
      .padding(0.15)
    const yMax = d3.max(buckets, (b) => b.total) || 1
    const y = d3.scaleLinear().domain([0, yMax]).nice().range([height, 0])

    // Recessive grid behind the bars.
    g.append('g')
      .call(d3.axisLeft(y).ticks(5).tickSize(-width).tickFormat(''))
      .call((sel) => sel.select('.domain').remove())
      .selectAll('line')
      .attr('stroke', '#e8e8e8')

    const isDaily = mode.daily
    const xTickEvery = isDaily ? (buckets.length > 20 ? 3 : buckets.length > 10 ? 2 : 1) : 3
    g.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(x).tickFormat((i) => {
        if (i % xTickEvery !== 0) return ''
        const m = moment(buckets[i].start)
        return isDaily ? m.format('MM/DD') : compactHourLabel(m.hour())
      }))
      .selectAll('text')
      .style('text-anchor', 'end')
      .attr('dx', '-.6em')
      .attr('dy', '.25em')
      .attr('transform', 'rotate(-45)')
      .call(styleAxisTicks, 9)
      .style('fill', 'black')
      .style('stroke', 'none')

    g.append('text')
      .attr('text-anchor', 'middle')
      .attr('x', width / 2)
      .attr('y', height + margin.bottom - 6)
      .attr('fill', 'black')
      .style('font-size', AXIS_TITLE_FONT_SIZE)
      .style('font-weight', AXIS_TITLE_FONT_WEIGHT)
      .text(isDaily ? 'Date' : 'Time of day')

    g.append('g')
      .call(d3.axisLeft(y).ticks(5).tickFormat(d3.format('d')))
      .selectAll('text')
      .style('fill', 'black')
      .style('stroke', 'none')
      .call(styleAxisTicks, 10)

    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', -margin.left + 14)
      .attr('x', -height / 2)
      .attr('text-anchor', 'middle')
      .attr('fill', 'black')
      .style('font-size', AXIS_TITLE_FONT_SIZE)
      .style('font-weight', AXIS_TITLE_FONT_WEIGHT)
      .text('Total reps')

    buckets.forEach((b) => {
      if (b.total === 0) return
      let acc = 0
      series.forEach((s) => {
        const v = b.reps[s.key] || 0
        if (v <= 0) return
        const yTop = y(acc + v)
        const yBot = y(acc)
        acc += v
        const rect = g.append('rect')
          .attr('x', x(b.index))
          .attr('y', yTop)
          .attr('width', x.bandwidth())
          .attr('height', Math.max(0, yBot - yTop))
          .attr('fill', s.color)
          // White hairline between stacked segments so adjacent colours read
          // as separate blocks, not one smeared bar.
          .attr('stroke', '#fff')
          .attr('stroke-width', 1)
          .style('cursor', isDaily ? 'zoom-in' : 'default')
        if (isDaily) {
          rect.on('click', () => {
            tooltip.style('visibility', 'hidden')
            setDay(moment(b.start).startOf('day'))
            setModeId('D1')
          })
        }
        rect.on('mouseover', () => {
          const label = isDaily
            ? moment(b.start).format('ddd MM/DD')
            : moment(b.start).format('MM/DD h A') + ' – ' + moment(b.start).add(1, 'hour').format('h A')
          const lines = series
            .filter((ss) => (b.reps[ss.key] || 0) > 0)
            .map((ss) => `<span style="color:${ss.color}">■</span> ${ss.label}: <b>${repsText(b.reps[ss.key], b.target[ss.key])}</b>`)
            .join('<br/>')
          const header = isDaily
            ? repsText(b.total, b.targetTotal)
            : `${b.total} reps (day: ${repsText(b.day.total, b.day.target)})`
          tooltip
            .style('visibility', 'visible')
            .html(`<b>${label}</b> · ${header}<br/>${lines}`)
            .style('top', Math.max(0, yTop - 10) + 'px')
            .style('left', Math.min(x(b.index) + x.bandwidth() + 10 + margin.left, screenWidth - 180) + 'px')
        })
        rect.on('mouseout', () => tooltip.style('visibility', 'hidden'))
      })
    })
  }

  const { buckets, mode } = buildBuckets()
  const hasAnyData = buckets.some((b) => b.total > 0)
  const firstDay = earliestDay(records, 'completedAt')
  // Same inputs the chart uses, so banner and chart agree.
  const stale = mode.daily ? effectiveWindow(records, mode.bars, repDate) : { isStale: false }

  const gridColumns = series
    .map((s) => ({ title: s.label, dataIndex: s.key, key: s.key }))
    .concat([{ title: 'Total', dataIndex: 'total', key: 'total' }])
  const gridRows = buckets
    .filter((b) => b.total > 0)
    .map((b) => {
      const cell = (done, target) => (target > 0 ? `${done}/${target}` : String(done))
      const row = { date: b.start, total: cell(b.total, b.targetTotal) }
      series.forEach((s) => { row[s.key] = b.reps[s.key] ? cell(b.reps[s.key], b.target[s.key]) : '—' })
      return row
    })

  return (
    <>
      <center>
        <h4>TracPatch Exercise Reps</h4>
      </center>
      {stale.isStale && (
        <Row justify='start' style={{ marginBottom: 8, paddingLeft: 12 }}>
          <div style={{ background:'#fff8e1', border:'1px solid var(--orange-color, #f5881f)',
                        borderRadius:8, padding:'6px 12px', fontSize:12, color:'#8a4b00', maxWidth:'100%' }}>
            {staleRangeLabel(stale.windowStart, stale.windowEnd, mode.bars)}
          </div>
        </Row>
      )}
      <Row justify='start' className='chartControls' style={{ marginBottom: 8, paddingLeft: 12 }}>
        <Button.Group size='small'>
          <Button type={modeId === 'D30' ? 'primary' : 'default'} onClick={() => setModeId('D30')}>30 Days</Button>
          <Button type={modeId === 'D14' ? 'primary' : 'default'} onClick={() => setModeId('D14')}>14 Days</Button>
          <Button type={modeId === 'D7' ? 'primary' : 'default'} onClick={() => setModeId('D7')}>7 Days</Button>
          <Button type={modeId === 'D1' ? 'primary' : 'default'} onClick={() => { setDay(moment().startOf('day')); setModeId('D1') }}>1 Day</Button>
        </Button.Group>
        <DataViewToggle
          active={view === 'data'}
          onClick={() => setView(view === 'data' ? 'chart' : 'data')}
        />
      </Row>
      {!mode.daily && (
        <Row justify='center' align='middle' className='chartDayNav' style={{ marginBottom: 8, gap: 8 }}>
          <Button
            size='small'
            aria-label='Previous day'
            icon={<CaretLeftOutlined />}
            disabled={!firstDay || !day.isAfter(firstDay)}
            onClick={() => setDay(day.clone().subtract(1, 'day').startOf('day'))}
          />
          <span style={{ minWidth: 150, textAlign: 'center', fontWeight: 600 }}>{day.format('ddd, MMM D, YYYY')}</span>
          <Button
            size='small'
            aria-label='Next day'
            icon={<CaretRightOutlined />}
            disabled={isTodayOrLater(day)}
            onClick={() => setDay(day.clone().add(1, 'day').startOf('day'))}
          />
        </Row>
      )}
      {series.length > 0 && (
        // HTML legend so it wraps in a half-width card.
        <Row style={{ gap: '4px 14px', padding: '0 12px 6px', fontSize: 11 }}>
          {series.map((s) => (
            <span key={s.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: s.color, display: 'inline-block' }} />
              {s.label}
            </span>
          ))}
        </Row>
      )}
      {hasAnyData ? (
        view === 'data' ? (
          <ChartDataGrid
            rows={gridRows}
            valueColumns={gridColumns}
            dateOnly={mode.daily}
            csvName='tracpatch-exercise-reps'
          />
        ) : (
          <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
            <div ref={tooltipRef} className='chartToolTip' style={{ position: 'absolute', visibility: 'hidden', zIndex: 10 }} />
            <svg style={{ width: '100%', height: '320px' }} ref={svgRef} />
          </div>
        )
      ) : (
        <Row justify='center' align='middle' style={{ height: 200 }}>
          <h2>Data not available</h2>
        </Row>
      )}
    </>
  )
}

const mapStateToProps = () => ({})
const mapDispatchToProps = (dispatch) => ({
  getTracpatchExerciseResults: (id) => dispatch(getTracpatchExerciseResults(id)),
})
export default connect(mapStateToProps, mapDispatchToProps)(TracPatchExerciseStackedChart)
